import { supabase } from '@/integrations/supabase/client';
import type { PrestadorServico } from '@/types/ordemServico';

export interface CriarPrestadorInput {
  profile_id: string;
  telefone?: string;
  servicos_oferecidos?: string[];
  observacoes?: string;
  orgId: string;
}

export interface AtualizarPrestadorInput {
  id: string;
  dados: Partial<{
    telefone: string;
    servicos_oferecidos: string[];
    observacoes: string;
  }>;
}

// Raw row shape from Supabase — servicos_oferecidos may be Json (array or unknown)
interface RawPrestadorRow {
  servicos_oferecidos: unknown;
  [key: string]: unknown;
}

function normalizePrestadorRow(row: RawPrestadorRow): PrestadorServico {
  return {
    ...row,
    servicos_oferecidos: Array.isArray(row.servicos_oferecidos)
      ? (row.servicos_oferecidos as string[])
      : [],
  } as unknown as PrestadorServico;
}

const PRESTADOR_SELECT = `
  *,
  profile:profiles!prestadores_servico_profile_id_fkey(id, nome, avatar_url, telefone, email, morada, codigo_postal, cidade, pais)
`;

// ============================================================================
// Queries
// ============================================================================

export async function listPrestadores(): Promise<PrestadorServico[]> {
  const { data, error } = await supabase
    .from('prestadores_servico')
    .select(PRESTADOR_SELECT)
    .order('criado_em', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(row => normalizePrestadorRow(row as unknown as RawPrestadorRow));
}

export async function getMeuCadastroPrestador(): Promise<PrestadorServico | null> {
  const { data: profileId, error: profileError } = await supabase.rpc('get_my_profile_id');
  if (profileError) throw profileError;
  if (!profileId) return null;

  const { data, error } = await supabase
    .from('prestadores_servico')
    .select(PRESTADOR_SELECT)
    .eq('profile_id', profileId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return normalizePrestadorRow(data as unknown as RawPrestadorRow);
}

export async function getPrestadorById(id: string): Promise<PrestadorServico> {
  const { data, error } = await supabase
    .from('prestadores_servico')
    .select(PRESTADOR_SELECT)
    .eq('id', id)
    .single();

  if (error) throw error;
  return normalizePrestadorRow(data as unknown as RawPrestadorRow);
}

// ============================================================================
// Mutations
// ============================================================================

export async function criarPrestador(input: CriarPrestadorInput): Promise<PrestadorServico> {
  const { data, error } = await supabase
    .from('prestadores_servico')
    .insert({
      profile_id: input.profile_id,
      telefone: input.telefone || null,
      servicos_oferecidos: input.servicos_oferecidos || [],
      observacoes: input.observacoes || '',
      organization_id: input.orgId,
    })
    .select()
    .single();

  if (error) throw error;
  return normalizePrestadorRow(data as unknown as RawPrestadorRow);
}

export async function atualizarPrestador(input: AtualizarPrestadorInput): Promise<PrestadorServico> {
  const { data, error } = await supabase
    .from('prestadores_servico')
    .update(input.dados)
    .eq('id', input.id)
    .select()
    .single();

  if (error) throw error;
  return normalizePrestadorRow(data as unknown as RawPrestadorRow);
}

